import React, { useMemo } from 'react';
import {
  Button,
  CircularProgress,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Table,
} from '@mui/material';

import { useAppSelector } from '../../../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';

import SubscriptionAPI from '../../../../../redux/incident/api/SubscriptionAPI';
import PolicyAPI from '../../../../../redux/incident/api/PolicyAPI';
import { PolicyAPIResponse } from '../../../../../redux/incident/api/Policy.model';
import { Base64 } from '../../../../../util/Base64';
import useWatchError from '../../../../../handlingErrors/useWatchError';
import { getPolyglot } from '../../../../../i18n';
import { getMyUserId } from '../../../../../redux/auth/selectors';

interface GroupSubscriptionListProps {
  groupId: string;
}

const GroupSubscriptionTable = (props: GroupSubscriptionListProps) => {
  const polyglot = getPolyglot();
  const { groupId } = props;

  const worksapce = useAppSelector(getMyWorkspaceSelector);
  const loggedUserId = useAppSelector(getMyUserId);

  const [
    unsubscribe,
    { error: unsubscribeError, isLoading: unsubscribeIsLoading },
  ] = SubscriptionAPI.useGroupUnsubscribeMutation();
  const [subscribe, { error: subscribeError, isLoading: subscribeIsLoading }] =
    SubscriptionAPI.useGroupSubscribeMutation();
  useWatchError(unsubscribeError || subscribeError);

  const policiesQuery = PolicyAPI.useGetPoliciesQuery(worksapce?.id);
  const policiesData = useMemo(
    () => ({
      policyList: policiesQuery.data || [],
    }),
    [policiesQuery.data]
  );

  const groupSubscriptionsQuery =
    SubscriptionAPI.useGetGroupSubscriptionsQuery(groupId);

  const groupSubscriptionData = useMemo(
    () => ({
      subscriptionList: groupSubscriptionsQuery.data || [],
    }),
    [groupSubscriptionsQuery.data]
  );

  const isLoading =
    unsubscribeIsLoading ||
    subscribeIsLoading ||
    policiesQuery.isLoading ||
    policiesQuery.isFetching ||
    groupSubscriptionsQuery.isLoading ||
    groupSubscriptionsQuery.isFetching;

  const handleSubscribe = async (policyId: string) => {
    await subscribe({ groupId, policyId });
  };

  const handleUnsubscribe = async (policyId: string) => {
    await unsubscribe({ groupId, policyId });
  };

  return (
    <>
      <Typography variant="h2">
        {polyglot.t('group.incident.subscriptions')}
      </Typography>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  {polyglot.t('group.incident.policy')}
                </TableCell>
                <TableCell align="center">
                  {polyglot.t('group.incident.actions')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {policiesData.policyList.map((policy: PolicyAPIResponse) => (
                <TableRow
                  key={`table-row-${policy.uuid}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="left">{policy.title ?? ''}</TableCell>
                  <TableCell align="center">
                    {groupSubscriptionData.subscriptionList
                      .filter(
                        (subscription) => subscription.policyId === policy.uuid
                      )
                      .map((user) => user.userId)
                      .includes(loggedUserId) ? (
                      <Button
                        color="primary"
                        variant="outlined"
                        type="button"
                        size="small"
                        onClick={() => handleUnsubscribe(policy.uuid)}
                        fullWidth
                      >
                        {polyglot.t('group.incident.unsubscribe')}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        variant="outlined"
                        type="button"
                        size="small"
                        onClick={() => handleSubscribe(policy.uuid)}
                        fullWidth
                      >
                        {polyglot.t('group.incident.subscribe')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default GroupSubscriptionTable;
